/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import Seo from '@salesforce/retail-react-app/app/components/seo';
// Components
import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
// Constants
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '@salesforce/retail-react-app/app/constants';
import useDataCloud from '@salesforce/retail-react-app/app/hooks/use-datacloud';
// Hooks
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein';

import Collections from '@amblique/king-living/app/components/collections';
import GalleryCarousel from '@amblique/king-living/app/components/gallery-carousel';
import HomepageBanner from '@amblique/king-living/app/components/homepage-banner';
// Project Components
import HomepageHeroBanner from '@amblique/king-living/app/components/homepage-hero-banner';

/**
 * This is the home page for King Living.
 * The page renders SEO metadata and features the new Homepage Hero Banner Block
 * along with promotion categories and products.
 */
const Home = () => {
  const einstein = useEinstein();
  const dataCloud = useDataCloud();
  const {pathname} = useLocation();

  const {res} = useServerContext();

  if (res) {
    res.set(
      'Cache-Control',
      `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
    );
  }

  /** ************** Einstein ****************/
  useEffect(() => {
    einstein.sendViewPage(pathname);
    dataCloud.sendViewPage(pathname);
  }, []);

  const handleShopSaleClick = () => {
    window.location.href = '/';
  };

  const handleGetInspiredClick = () => {
    window.location.href = '/';
  };

  const styles = useMultiStyleConfig('Home', {});

  return (
    <Box data-testid="home-page" {...styles.container}>
      <Seo
        description="King Living - Premium Furniture and Design"
        keywords="King Living, furniture, premium design, home decor"
        title="King Living - Home"
      />

      {/* Homepage Hero Banner Block */}
      <HomepageHeroBanner
        primaryButtonText="Shop sale"
        secondaryButtonText="Get inspired"
        subtitle="Up to 50% off selected designs"
        title="Summer sale ends soon"
        onPrimaryClick={handleShopSaleClick}
        onSecondaryClick={handleGetInspiredClick}
      />

      {/* Homepage Banner Block */}
      <HomepageBanner
        backgroundImage={getAssetUrl('static/img/banner-bg-1.png')}
        primaryButtonText="Find out more"
        subtitle="A playful, children's-sized version of the timeless 1977 Sofa, the 1977 Mini is perfect for bedrooms or playrooms."
        title="1977 Mini"
      />

      {/* Homepage Banner Block */}
      <HomepageBanner
        backgroundImage={getAssetUrl('static/img/banner-bg-2.png')}
        primaryButtonText="Find out more"
        subtitle="Effortless elegance meets ultimate comfort. The Zaza Sofa’s deep, luxurious seats and sleek design invite you to relax in style."
        title="Zaza"
      />

      {/* Shop the Look Gallery */}
      <GalleryCarousel />

      {/* Collections Section */}
      <Collections />
    </Box>
  );
};

Home.getTemplateName = () => 'home';

export default Home;
